import { useId, useState, useRef, useEffect, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Champ texte libre avec suggestions. Différent d'AutocompleteField :
 * la valeur est une chaîne de caractères (pas un id), et le user peut
 * taper n'importe quoi — la liste de suggestions sert juste à éviter
 * les doublons (typo, casse, espaces) en montrant les valeurs existantes
 * matchant la saisie.
 *
 * Le dropdown apparaît dès que `value.length >= minChars` (défaut 2).
 */
interface FreeTextAutocompleteFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label: string;
  value?: string;
  suggestions: string[];
  onChange?: (value: string) => void;
  error?: string;
  minChars?: number;
  highlight?: boolean;
  /** Hint visuel pour la validation EN PRÉPARATION (cf. lib/bienPrepValidation.ts). */
  missingHint?: "required" | "mailing";
  /**
   * Même contrat que `SelectField.readOnly`, pour que le helper `F()` des
   * fiches verrouille aussi ce champ. Sans ça, `readOnly` passé nativement à
   * `<input>` bloque la frappe clavier mais PAS le focus : `onFocus` ouvrait
   * quand même le dropdown de suggestions, et cliquer une suggestion
   * modifiait la valeur malgré le droit "Visible" — le verrouillage par
   * droits était donc réellement inopérant sur ce champ.
   */
  readOnly?: boolean;
}

export function FreeTextAutocompleteField({
  label,
  value = "",
  suggestions,
  onChange,
  error,
  className,
  minChars = 2,
  // Prop héritée — cf. note dans FormField.tsx (commit 064ca14).
  highlight: _highlight,
  missingHint,
  readOnly = false,
  ...props
}: FreeTextAutocompleteFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const inputId = props.id ?? generatedId;

  const isFilled = value.length > 0;
  const trimmed = value.trim();
  const lcTrimmed = trimmed.toLowerCase();

  // Filtre case-insensitive, exclut les matches exacts (déjà saisis tels quels)
  const filtered = trimmed.length >= minChars
    ? suggestions.filter(
        (s) => s && s.toLowerCase().includes(lcTrimmed) && s.toLowerCase() !== lcTrimmed,
      )
    : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const showMissingBorder = !isFilled && !isFocused && !error && !!missingHint;
  const missingBorderClass =
    missingHint === "required"
      ? "border border-solid border-danger"
      : missingHint === "mailing"
        ? "border border-solid border-warning"
        : "";

  const borderClasses = error
    ? "border border-solid border-danger"
    : showMissingBorder
      ? missingBorderClass
      : isFocused
        ? "border border-solid border-border-focus"
        : isFilled && isHovered
          ? "border border-dashed border-border-hover"
          : isFilled
            ? "border border-solid border-transparent"
            : isHovered
              ? "border border-dashed border-border-hover"
              : "border border-dashed border-border";

  // On ne colore le label QUE si le champ est vide ET tagué par le système de
  // validation EN PRÉPARATION/DISPONIBLE. Le prop highlight (c_level pré-existant)
  // n'affecte plus la couleur depuis qu'on a retiré sa légende.
  // Priorité : required > mailing > muted.
  const labelColorClass = isFilled
    ? "text-text-muted"
    : missingHint === "required"
      ? "text-danger"
      : missingHint === "mailing"
        ? "text-warning"
        : "text-text-muted";

  const showDropdown = !readOnly && isOpen && filtered.length > 0;

  return (
    <div className={cn("flex flex-col relative", className)} ref={wrapperRef}>
      <label htmlFor={inputId} className={cn("text-xs font-medium tracking-wider uppercase px-3 mb-1", labelColorClass)}>
        {label}
      </label>
      <input
        id={inputId}
        className={cn(
          "px-3 py-2 bg-surface-2 rounded-md text-sm",
          isFilled ? "text-text-primary" : "text-text-muted",
          "placeholder:text-text-muted outline-none shadow-none",
          "transition-colors duration-150",
          borderClasses,
        )}
        value={value}
        readOnly={readOnly}
        onChange={(e) => {
          if (readOnly) return;
          onChange?.(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setIsFocused(true);
          if (!readOnly) setIsOpen(true);
        }}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setIsOpen(false);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        placeholder="..."
        {...props}
      />
      {showDropdown && (
        <div className="relative z-10">
          <div className="absolute top-1 left-0 right-0 bg-surface border border-border rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-h-[240px] overflow-y-auto">
            {filtered.map((s) => (
              <button
                key={s}
                type="button"
                className="w-full px-3 py-2 text-sm text-left hover:bg-gray-200 transition-colors cursor-pointer"
                // mousedown évite la course avec le blur de l'input
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange?.(s);
                  setIsOpen(false);
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </div>
  );
}
